import React, { useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { injectIntl } from '../../util/reactIntl';

import config from '../../config';
import { FaMapMarkerAlt, FaStar } from 'react-icons/fa';
import { IoLanguage } from 'react-icons/io5';

import { Tooltip, AvatarLarge } from '../../components';

import css from './ProfileUserCard.css';

// Custom hook for profile data
const useProfileData = profileUser => {
  return useMemo(() => {
    const profile = profileUser?.attributes?.profile;
    const publicData = profile?.publicData || {};

    return {
      displayName: profile?.displayName || '',
      location: publicData.location || '',
      languages: publicData.languages || [],
    };
  }, [profileUser]);
};

// Custom hook for reviews data
const useReviewsData = combinedReviews => {
  return useMemo(() => {
    if (!combinedReviews?.length) return null;

    const averageRating = (
      combinedReviews.reduce((a, b) => a + b?.rating, 0) / combinedReviews.length
    ).toFixed(1);

    return {
      averageRating,
      reviewCount: combinedReviews.length,
    };
  }, [combinedReviews]);
};

// Custom hook for languages
const useLanguagesData = languages => {
  const languageOptions = config.custom.filters[3].config.options;

  return useMemo(() => {
    if (!languages?.length) return null;

    const keys = languages.map(l => (typeof l === 'string' ? l : l?.language)).filter(Boolean);

    if (!keys.length) return null;

    return keys.map((key, index, arr) => (
      <span key={key}>
        {languageOptions.find(l => l.key === key)?.label || key}
        {index < arr.length - 1 ? ', ' : ''}
      </span>
    ));
  }, [languages, languageOptions]);
};

// Reviews component
const ReviewsSection = ({ reviewsData }) => {
  if (!reviewsData) return null;

  return (
    <Tooltip messageId="Tooltip.reviews">
      <div className={css.reviews}>
        <FaStar className={css.starIcon} />
        {reviewsData.averageRating}
        &nbsp;({reviewsData.reviewCount})
      </div>
    </Tooltip>
  );
};

// Profile image component
const ProfileImageSection = ({ profileUser }) => (
  <div className={css.profileImageContainer}>
    <AvatarLarge user={profileUser} className={css.profileImage} />
  </div>
);

// Who am I section component
const WhoAmISection = ({ displayName, reviewsData }) => (
  <div className={css.whoAmIContainer}>
    <div className={css.displayName}>{displayName}</div>
    {reviewsData && (
      <div className={css.reviewAndTestimonials}>
        <ReviewsSection reviewsData={reviewsData} />
      </div>
    )}
  </div>
);

// Location section component
const LocationSection = ({ location }) => {
  if (!location || location === '' || location?.selectedPlace === null) return null;

  return (
    <div className={css.locationSection}>
      <FaMapMarkerAlt className={css.mapMarkerIcon} />
      {location?.search.split(',')[0]}
    </div>
  );
};

// Languages section component
const LanguagesSection = ({ languagesString }) => {
  if (!languagesString) return null;

  return (
    <div className={css.languageSection}>
      <IoLanguage className={css.languageIcon} />
      <div className={css.languageContainer}>{languagesString}</div>
    </div>
  );
};

const BuyerProfileUserCardComponent = ({ profileUser, combinedReviews }) => {
  const profileData = useProfileData(profileUser);
  const reviewsData = useReviewsData(combinedReviews);
  const languagesString = useLanguagesData(profileData.languages);

  return (
    <div className={css.profileUserCard}>
      <ProfileImageSection profileUser={profileUser} />
      <WhoAmISection displayName={profileData.displayName} reviewsData={reviewsData} />
      <LocationSection location={profileData.location} />
      <LanguagesSection languagesString={languagesString} />
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const BuyerProfileUserCard = compose(
  connect(null, mapDispatchToProps),
  injectIntl
)(BuyerProfileUserCardComponent);

export default BuyerProfileUserCard;
